import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Code2, EyeOff, FileText, History, Minus, Quote,
  Save, Send, Square, Upload, Wand2, X,
} from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui';
import { Spinner } from '@/components/ui/spinner';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { useNotification } from '@/context/NotificationProvider';
import {
  useFinalidades,
  useTemplate,
  useTemplateVersions,
  useCreateVersion,
  useRestoreVersion,
  useTemplatePreview,
  useSendTestEmail,
  useUpdateBinding,
} from '@/hooks/useTemplates';

const EMAIL_HELPERS = [
  { syntax: '{{ botao("Texto", url) }}', label: 'Botão', icon: Square },
  { syntax: '{{ botao_alerta("Texto", url) }}', label: 'Botão Alerta', icon: Square },
  { syntax: '{{ divisor() }}', label: 'Divisor', icon: Minus },
  { syntax: '{{ destaque("Texto aqui") }}', label: 'Destaque', icon: Quote },
];

/**
 * Wrapper que força remount do editor quando a versão ativa muda.
 */
export default function TemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { data: template, isLoading, error } = useTemplate(id!);

  if (isLoading) return <Spinner size={32} label="Carregando template" />;
  if (error) return <Alert variant="destructive"><AlertDescription>{classifyError(error).message}</AlertDescription></Alert>;
  if (!template) return null;

  const versionKey = template.versao_atual?.id ?? 'no-version';
  return <TemplateEditor key={versionKey} templateId={id!} initialContent={template.versao_atual?.conteudo ?? ''} />;
}

function TemplateEditor({ templateId, initialContent }: { templateId: string; initialContent: string }) {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Queries e mutations
  const { data: template } = useTemplate(templateId);
  const { data: versoes } = useTemplateVersions(templateId);
  const { data: finalidades } = useFinalidades();
  const createVersionMutation = useCreateVersion(templateId);
  const restoreMutation = useRestoreVersion(templateId);
  const previewMutation = useTemplatePreview();
  const testEmailMutation = useSendTestEmail();
  const updateBindingMutation = useUpdateBinding();

  // Estado local
  const [conteudo, setConteudo] = useState(initialContent);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showTestEmail, setShowTestEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  // Derivados
  const isPrompt = template?.categoria.startsWith('PROMPT') ?? false;
  const finalidade = template?.finalidade_id
    ? finalidades?.find((f) => f.nome === template.finalidade_id) ?? null
    : null;
  const bindingVersao = finalidade?.binding_atual?.versao_numero ?? null;
  const currentVersao = template?.versao_atual?.versao ?? null;
  const isPublished = currentVersao != null && bindingVersao === currentVersao;
  const variaveisDisponiveis = template?.variaveis_disponiveis ?? [];

  // Valores primitivos para dependências do efeito (evita problemas de referência)
  const templateCategoria = template?.categoria;
  const templateFinalidadeId = template?.finalidade_id;
  const isFirstRender = useRef(true);

  // Live preview: imediato no mount, debounced (500ms) nas edições
  useEffect(() => {
    if (isPrompt || !templateCategoria || !conteudo) {
      setPreviewHtml('');
      return;
    }

    const delay = isFirstRender.current ? 0 : 500;
    isFirstRender.current = false;

    const categoria = templateCategoria;
    const finalidade_id = templateFinalidadeId || undefined;

    const timer = setTimeout(() => {
      previewMutation.mutate(
        { conteudo, categoria, finalidade_id },
        { onSuccess: (data) => setPreviewHtml(data.html) },
      );
    }, delay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conteudo, isPrompt, templateCategoria, templateFinalidadeId]);

  const handleSave = () => {
    createVersionMutation.mutate(conteudo, {
      onSuccess: () => showSuccess('Nova versão salva'),
      onError: (err) => notifyMutationError(err, showError),
    });
  };

  const handleRestore = (versaoId: string) => {
    restoreMutation.mutate(versaoId, {
      onSuccess: () => showSuccess('Versão restaurada'),
      onError: (err) => notifyMutationError(err, showError),
    });
  };

  const handleSendTestEmail = () => {
    if (!testEmail || !template) return;
    testEmailMutation.mutate(
      {
        conteudo,
        categoria: template.categoria,
        finalidade_id: template.finalidade_id ?? undefined,
        to_email: testEmail,
      },
      {
        onSuccess: () => {
          showSuccess(`Email de teste enfileirado para ${testEmail}`);
          setShowTestEmail(false);
          setTestEmail('');
        },
        onError: (err) => notifyMutationError(err, showError),
      },
    );
  };

  const handlePublish = () => {
    if (!template?.finalidade_id || !template?.versao_atual?.id) return;
    updateBindingMutation.mutate(
      { nome: template.finalidade_id, versaoId: template.versao_atual.id },
      {
        onSuccess: () => showSuccess('Versão publicada em produção'),
        onError: (err) => notifyMutationError(err, showError),
      },
    );
  };

  const insertAtCursor = useCallback((text: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setConteudo((prev) => prev + text);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    setConteudo((prev) => prev.substring(0, start) + text + prev.substring(end));
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + text.length, start + text.length);
    });
  }, []);

  if (!template) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader
        title={template.nome}
        subtitle={template.finalidade_descricao ?? template.finalidade_id ?? ''}
        icon={<FileText className="h-6 w-6" />}
      />

      {/* Info card */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          {template.finalidade_id && (
            <div>
              <span className="text-muted-foreground text-xs uppercase">Finalidade</span>
              <p className="font-mono text-xs">{template.finalidade_id}</p>
            </div>
          )}
          <div>
            <span className="text-muted-foreground text-xs uppercase">Categoria</span>
            <p><Badge variant={isPrompt ? 'secondary' : 'default'}>{template.categoria}</Badge></p>
          </div>
          <div>
            <span className="text-muted-foreground text-xs uppercase">Versão</span>
            <p>{currentVersao != null ? `v${currentVersao}` : '—'}</p>
          </div>
          {template.finalidade_id && (
            <div>
              <span className="text-muted-foreground text-xs uppercase">Produção</span>
              <p>
                {isPublished ? (
                  <Badge className="bg-green-600 hover:bg-green-600">v{bindingVersao}</Badge>
                ) : bindingVersao != null ? (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">v{bindingVersao}</Badge>
                ) : (
                  <span className="text-muted-foreground italic">Não publicado</span>
                )}
              </p>
            </div>
          )}
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowHistory(!showHistory)}>
              <History className="h-4 w-4 mr-1" /> Histórico
            </Button>
            {template.finalidade_id && !isPublished && (
              <Button
                size="sm"
                variant="outline"
                onClick={handlePublish}
                disabled={updateBindingMutation.isPending || !template.versao_atual}
              >
                <Upload className="h-4 w-4 mr-1" />
                Publicar v{currentVersao}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Editor + Preview */}
      <div className={`grid gap-4 ${isPrompt ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
        {/* Coluna do Editor */}
        <div className="flex flex-col border rounded-lg overflow-hidden min-h-[500px]">
          {/* Toolbar */}
          <div className="flex flex-wrap gap-3 px-3 py-2 bg-muted/30 border-b">
            {variaveisDisponiveis.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  <Code2 className="inline h-3 w-3 mr-0.5" />Variáveis:
                </span>
                {variaveisDisponiveis.map((v) => (
                  <Button
                    key={v}
                    type="button"
                    variant="unstyled"
                    onClick={() => insertAtCursor(`{{${v}}}`)}
                    className="h-auto px-2 py-0.5 rounded text-[11px] font-mono bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground"
                    title={`Inserir {{${v}}}`}
                  >
                    {v}
                  </Button>
                ))}
              </div>
            )}
            {!isPrompt && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  <Wand2 className="inline h-3 w-3 mr-0.5" />Helpers:
                </span>
                {EMAIL_HELPERS.map((h) => (
                  <Button
                    key={h.syntax}
                    type="button"
                    variant="unstyled"
                    onClick={() => insertAtCursor(h.syntax)}
                    className="h-auto gap-1 px-2 py-0.5 rounded text-[11px] border border-border text-muted-foreground hover:border-primary hover:text-primary [&_svg]:size-3"
                    title={h.syntax}
                  >
                    <h.icon className="h-3 w-3" />
                    {h.label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={conteudo}
            onChange={(e) => setConteudo(e.target.value)}
            className="flex-1 w-full p-3 border-0 resize-none font-mono text-[13px] leading-relaxed bg-card focus:outline-none"
            placeholder={isPrompt ? 'Digite o prompt aqui...' : 'Digite o HTML do template aqui...'}
            spellCheck={false}
            style={{ tabSize: 2 }}
          />
        </div>

        {/* Coluna do Preview (apenas email/layout) */}
        {!isPrompt && (
          <div className="flex flex-col border rounded-lg overflow-hidden min-h-[500px]">
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b text-sm font-medium shrink-0">
              <span>Preview</span>
              {previewMutation.isPending && <Spinner />}
              <div className="ml-auto">
                {showTestEmail ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendTestEmail()}
                      placeholder="Digite o email..."
                      className="h-7 w-44 text-xs"
                      disabled={testEmailMutation.isPending}
                    />
                    <Button
                      size="icon"
                      variant="default"
                      className="h-7 w-7"
                      onClick={handleSendTestEmail}
                      disabled={testEmailMutation.isPending || !testEmail}
                    >
                      {testEmailMutation.isPending
                        ? <Spinner className="text-inherit" />
                        : <Send className="h-3.5 w-3.5" />
                      }
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => { setShowTestEmail(false); setTestEmail(''); }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => setShowTestEmail(true)}
                  >
                    <Send className="h-3.5 w-3.5 mr-1" />
                    Enviar Teste
                  </Button>
                )}
              </div>
            </div>
            <div className="flex-1 bg-white">
              {previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-full border-0 min-h-[460px]"
                  title="Preview do template"
                  sandbox=""
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-muted-foreground gap-2">
                  <EyeOff className="h-6 w-6 opacity-50" />
                  <span className="text-sm">Digite algo para ver o preview</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Ações */}
      <div className="flex items-center justify-between border-t pt-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
        </Button>
        <Button onClick={handleSave} disabled={createVersionMutation.isPending}>
          {createVersionMutation.isPending
            ? <Spinner className="mr-1 text-inherit" />
            : <Save className="h-4 w-4 mr-1" />
          }
          Salvar Nova Versão
        </Button>
      </div>

      {/* Histórico de versões */}
      {showHistory && versoes && (
        <Card className="p-4 space-y-2">
          <h3 className="font-medium text-sm">Histórico de versões</h3>
          {versoes.map((v) => (
            <div key={v.id} className="flex items-center justify-between py-1 text-sm">
              <span>
                v{v.versao} — {new Date(v.created_at).toLocaleDateString('pt-BR')}
                {v.created_by_nome && (
                  <span className="text-muted-foreground ml-1">por {v.created_by_nome}</span>
                )}
                {v.is_current && <Badge variant="default" className="ml-2">Atual</Badge>}
              </span>
              {!v.is_current && (
                <Button size="sm" variant="ghost" onClick={() => handleRestore(v.id)}>
                  Restaurar
                </Button>
              )}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
